import { DefaultBasePage } from '@components';
import {
  addPhoneNumberValidation,
  Button,
  MaskedTextFieldV2,
  TextFieldV2,
  useSnackbar,
  convertMaskToE164,
  maskPhone,
  DatePickerV2,
  formatDate,
  Typography,
} from '@fdha/web-ui-library';
import {
  GetProfileDocument,
  useGetProfileQuery,
  UserType,
  useUpdateProfileMutation,
} from '@fdha/graphql-api-patient';
import { Box, Stack, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { usePrompt } from '@hooks';
import { utcToZonedTime } from 'date-fns-tz';
import { getTranslatedErrorMessage } from '@fdha/common-utils';

interface Profile {
  name: string;
  email: string;
  phoneNumber: string;
  birthDate: Date | null;
}

const YourAccount = () => {
  const requiredMessage = getTranslatedErrorMessage('required', 'web');

  const theme = useTheme();

  const navigate = useNavigate();
  const { showSnackbarV2 } = useSnackbar();

  const { data } = useGetProfileQuery();
  const [updateProfile] = useUpdateProfileMutation();

  const formattedAddress = data?.me?.address?.formatted || '';

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required(requiredMessage),
    email: Yup.string()
      .trim()
      .email(getTranslatedErrorMessage('validEmail', 'web'))
      .required(requiredMessage),
    phoneNumber: addPhoneNumberValidation(
      getTranslatedErrorMessage('validPhone', 'web')
    ).required(requiredMessage),
    birthDate: Yup.date()
      .nullable(true)
      .typeError(getTranslatedErrorMessage('validDate', 'web'))
      .required(requiredMessage),
  });

  const isCt = data?.me.type === UserType.ClinicalTrialPatient;

  const initialValues: Profile = {
    name: data?.me.name || '',
    email: data?.me.email || '',
    phoneNumber: maskPhone(data?.me.phone_number),
    birthDate: data?.me.birthdate
      ? new Date(utcToZonedTime(data.me.birthdate, 'UTC'))
      : null,
  };

  const handleErrors = (
    errors: FormikErrors<Profile>,
    touched: FormikTouched<Profile>
  ) => {
    return {
      name: touched.name && errors.name,
      email: touched.email && errors.email,
      phoneNumber: touched.phoneNumber && errors.phoneNumber,
      birthDate: touched.birthDate && errors.birthDate,
    };
  };

  const handleCancel = () => {
    showSnackbarV2({
      message: 'Changes Not Saved',
      severity: 'info',
      i18nKey: 'common:snackbar.changesNotSaved',
    });
    navigate('/profile', { replace: true });
  };

  const handleSubmit = async (values: Profile) => {
    try {
      await updateProfile({
        variables: {
          props: {
            name: values.name?.trim().replace(/\s+/g, ' '),
            email: values.email?.trim(),
            phone_number: convertMaskToE164(values.phoneNumber),
            birthdate: formatDate(values.birthDate),
          },
        },
        refetchQueries: [GetProfileDocument],
      });

      showSnackbarV2({
        message: 'Changes Saved',
        severity: 'success',
        i18nKey: 'common:snackbar.changesSaved',
      });
    } catch (error) {
      console.error(error);
      showSnackbarV2({
        message: 'Error to update profile',
        severity: 'error',
        i18nKey: 'profile:yourAccount.snackbar.errorUpdateProfile',
      });
    }
  };

  const {
    errors,
    isSubmitting,
    values,
    touched,
    dirty,
    handleBlur,
    handleChange,
    handleSubmit: FormikHandleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const error = handleErrors(errors, touched);

  usePrompt(dirty);

  return (
    <DefaultBasePage
      title="Your Account"
      contentSize="small"
      i18nKeyTitle="profile:yourAccount.title"
    >
      <Box display="flex" justifyContent="center" flexDirection="column">
        <Stack spacing={2} mt="22px">
          <TextFieldV2
            name="name"
            i18nKeyTitle="profile:yourAccount.input.name.label"
            title="Name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!error.name}
            helperText={error.name}
          />
          <DatePickerV2
            title="Birth date"
            value={values.birthDate}
            i18nKeyTitle="profile:yourAccount.input.birthDate.label"
            locale={data?.me?.language}
            onBlur={handleBlur}
            error={!!error.birthDate}
            helperText={error.birthDate}
            onChange={(value) => {
              setFieldValue('birthDate', value);
            }}
          />
          <MaskedTextFieldV2
            name="phoneNumber"
            i18nKeyTitle="profile:yourAccount.input.mobileNumber.label"
            title="Mobile number"
            mask="phoneNumber"
            i18nKeyPlaceholder="profile:yourAccount.input.mobileNumber.placeholder"
            placeholder="xxx-xxx-xxxx"
            value={values.phoneNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!error.phoneNumber}
            helperText={error.phoneNumber}
          />
          <TextFieldV2
            name="email"
            i18nKeyTitle="profile:yourAccount.input.email.label"
            title="Email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!error.email}
            helperText={error.email}
          />
          {isCt && (
            <>
              <Stack spacing={0.25}>
                <Typography
                  variant="subtitle2"
                  i18nKey="profile:yourAccount.input.address.label"
                >
                  Address
                </Typography>
                {formattedAddress ? (
                  <Typography variant="body1">{formattedAddress}</Typography>
                ) : (
                  <Typography
                    variant="body2"
                    i18nKey="profile:input.address.emptyState"
                    color={theme.palette.text.secondary}
                  >
                    [Not provided]
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  i18nKey="profile:input.address.contactCoach"
                  color={theme.palette.text.secondary}
                >
                  Please contact your coach to update your address.
                </Typography>
              </Stack>
            </>
          )}
          <Stack spacing={2} direction="row" justifyContent="flex-end">
            <Button onClick={handleCancel} i18nKey="common:button.cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              onClick={() => FormikHandleSubmit()}
              sx={{ width: '140px' }}
              i18nKey="common:button.save"
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    </DefaultBasePage>
  );
};

export default YourAccount;
